import { CandidateReferenceBadge } from "@components/candidates/CandidateReferenceBadge";
import { CollapsibleSection } from "@components/shared/CollapsibleSection";
import {
  ADD_CANDIDATE_TO_CAMPAIGN,
  ADD_TO_TALENT_POOL,
  CREATE_CANDIDATE,
  IMPORT_LINKEDIN_PROFILE,
  IMPORT_LINKEDIN_TO_CAMPAIGN,
  IMPORT_LINKEDIN_TO_TALENT_POOL,
} from "@lib/graphql/mutations";
import { CANDIDATE_DUPLICATE_CHECK_QUERY, CANDIDATE_SEARCH_QUERY } from "@lib/graphql/queries";
import { useDebounce } from "@lib/hooks/useDebounce";
import { type BackgroundTask, useBackgroundTasksStore } from "@lib/stores/backgroundTasksStore";
import { ArrowLeft, Linkedin, Loader2, Plus, Search, UserPlus, X } from "lucide-react";
import { useId, useState } from "react";
import { Link } from "react-router";
import { useClient, useMutation, useQuery } from "urql";
import {
  type AddTarget,
  buildAddTo,
  buildCandidateInput,
  describeMembership,
  membershipOf,
  parseLinkedinUrl,
  parseNameFromSearch,
  type SearchCandidate,
} from "./addCandidateHelpers";
import {
  CandidateFormFields,
  type CandidateFormState,
  INITIAL_CANDIDATE_FORM,
} from "./CandidateFormFields";

export type { AddTarget } from "./addCandidateHelpers";

interface AddCandidateToListDialogProps {
  target: AddTarget;
  /**
   * Called when the dialog has done its job. `candidateId` is set when a
   * candidate was created or added synchronously; it is undefined when a
   * LinkedIn import was queued (the candidate does not exist yet).
   */
  onDone: (candidateId?: string) => void;
  onCancel: () => void;
  heading?: string;
}

type DialogPhase =
  | { step: "main" }
  | { step: "duplicateReview"; duplicates: SearchCandidate[] }
  | { step: "importing"; taskId: string; url: string };

function defaultHeading(target: AddTarget): string {
  switch (target.kind) {
    case "org":
      return "Add candidate";
    case "talent_pool":
      return "Add candidate to pool";
    case "campaign":
      return `Add candidate to ${target.stage}`;
  }
}

function importCtaLabel(target: AddTarget): string {
  switch (target.kind) {
    case "org":
      return "Import from LinkedIn";
    case "talent_pool":
      return "Import from LinkedIn & add to pool";
    case "campaign":
      return `Import from LinkedIn & add to ${target.stage}`;
  }
}

function importedSummary(name: string | undefined, target: AddTarget): string {
  const who = name ?? "Profile";
  switch (target.kind) {
    case "org":
      return `${who} imported.`;
    case "talent_pool":
      return `${who} imported and added to the pool.`;
    case "campaign":
      return `${who} imported and added to the ${target.stage}.`;
  }
}

function addLabel(target: AddTarget): string {
  return target.kind === "talent_pool" ? "Add to pool" : "Add to campaign";
}

function ResultRow({ candidate, action }: { candidate: SearchCandidate; action: React.ReactNode }) {
  return (
    <div className="flex w-full items-center justify-between border-b border-border px-3 py-2 text-left text-sm">
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline gap-2">
          {typeof candidate.referenceNumber === "number" && (
            <CandidateReferenceBadge referenceNumber={candidate.referenceNumber} />
          )}
          <span className="font-medium text-text-primary">{candidate.fullName}</span>
          {(candidate.currentRoleTitle || candidate.currentCompany) && (
            <span className="truncate text-xs text-text-muted">
              {[candidate.currentRoleTitle, candidate.currentCompany].filter(Boolean).join(" at ")}
            </span>
          )}
        </div>
        {(candidate.campaignCandidates.length > 0 ||
          candidate.talentPoolMemberships.length > 0) && (
          <div className="mt-1 flex flex-wrap gap-1">
            {candidate.campaignCandidates.map((cc) => (
              <span
                key={cc.id}
                className="inline-flex items-center rounded-full bg-accent/10 px-2 py-0.5 text-[10px] font-medium text-accent"
              >
                {cc.campaign.title} &middot; {cc.stage}
              </span>
            ))}
            {candidate.talentPoolMemberships.map((m) => (
              <span
                key={m.id}
                className="inline-flex items-center rounded-full bg-text-muted/10 px-2 py-0.5 text-[10px] font-medium text-text-muted"
              >
                {m.talentPool.name}
              </span>
            ))}
          </div>
        )}
      </div>
      {action}
    </div>
  );
}

function OpenLink({ candidateId }: { candidateId: string }) {
  return (
    <Link
      to={`/candidates/${candidateId}`}
      className="ml-2 flex-shrink-0 text-xs text-accent hover:underline"
    >
      Open
    </Link>
  );
}

const SECONDARY_BUTTON =
  "rounded-md border border-input-border px-3 py-1.5 text-xs font-medium text-text-secondary hover:bg-card-hover disabled:opacity-50";
const PRIMARY_BUTTON =
  "rounded-md bg-accent px-3 py-1.5 text-xs font-medium text-white hover:bg-accent-hover disabled:opacity-50";

export function AddCandidateToListDialog({
  target,
  onDone,
  onCancel,
  heading,
}: AddCandidateToListDialogProps) {
  const [search, setSearch] = useState("");
  const [importUrl, setImportUrl] = useState("");
  const [importUrlDirty, setImportUrlDirty] = useState(false);
  const [manualOpen, setManualOpen] = useState(false);
  const [focusManual, setFocusManual] = useState(false);
  const [error, setError] = useState("");
  const [phase, setPhase] = useState<DialogPhase>({ step: "main" });
  const [form, setForm] = useState<CandidateFormState>(INITIAL_CANDIDATE_FORM);
  const [checking, setChecking] = useState(false);
  const importInputId = useId();

  const client = useClient();

  const trimmed = search.trim();
  const searchIsUrl = parseLinkedinUrl(trimmed) !== null;
  const debouncedName = useDebounce(searchIsUrl ? "" : trimmed);
  const nameTerm = debouncedName.length >= 2 ? debouncedName : null;
  const parsedImportUrl = parseLinkedinUrl(importUrl);

  const [{ data: nameData, fetching: nameFetching }] = useQuery({
    query: CANDIDATE_SEARCH_QUERY,
    variables: { search: nameTerm ?? undefined },
    pause: nameTerm === null || phase.step !== "main",
  });
  const [{ data: urlData, fetching: urlFetching }] = useQuery({
    query: CANDIDATE_SEARCH_QUERY,
    variables: { search: parsedImportUrl ?? undefined },
    pause: parsedImportUrl === null || phase.step !== "main",
  });

  const [{ fetching: addingToCampaign }, addToCampaign] = useMutation(ADD_CANDIDATE_TO_CAMPAIGN);
  const [{ fetching: addingToPool }, addToPool] = useMutation(ADD_TO_TALENT_POOL);
  const [{ fetching: importingToCampaign }, importToCampaign] = useMutation(
    IMPORT_LINKEDIN_TO_CAMPAIGN,
  );
  const [{ fetching: importingToPool }, importToPool] = useMutation(IMPORT_LINKEDIN_TO_TALENT_POOL);
  const [{ fetching: importingToOrg }, importToOrg] = useMutation(IMPORT_LINKEDIN_PROFILE);
  const [{ fetching: creating }, createCandidate] = useMutation(CREATE_CANDIDATE);

  const adding = addingToCampaign || addingToPool;
  const importing = importingToCampaign || importingToPool || importingToOrg;

  const nameCandidates: SearchCandidate[] = nameData?.candidateSearch ?? [];
  const nameSearchReady = nameTerm !== null && !nameFetching;
  const urlMatch: SearchCandidate | undefined =
    parsedImportUrl && !urlFetching ? urlData?.candidateSearch?.[0] : undefined;

  const upsertBackgroundTask = useBackgroundTasksStore((s) => s.upsert);

  // While an import runs the dialog stays open and follows its task in the
  // store (fed by the BackgroundTaskChannel), then shows the imported
  // candidate rather than dropping the user back to a list they may not
  // find them in.
  const importPhase = phase.step === "importing" ? phase : null;
  const importTask = useBackgroundTasksStore((s) =>
    importPhase ? s.tasksById[importPhase.taskId] : undefined,
  );
  const importState = importTask?.state ?? "running";
  const [{ data: importedData, fetching: importedFetching }] = useQuery({
    query: CANDIDATE_SEARCH_QUERY,
    variables: { search: importPhase?.url },
    pause: !importPhase || importState !== "succeeded",
    requestPolicy: "network-only",
  });
  const importedCandidate: SearchCandidate | undefined =
    importPhase && importState === "succeeded" ? importedData?.candidateSearch?.[0] : undefined;

  const resetToMain = () => {
    setPhase({ step: "main" });
    setSearch("");
    setImportUrl("");
    setImportUrlDirty(false);
    setError("");
  };

  const handleSearchChange = (value: string) => {
    setSearch(value);
    const parsed = parseLinkedinUrl(value);
    if (parsed) {
      if (!importUrlDirty) setImportUrl(parsed);
    } else if (!importUrlDirty && importUrl) {
      setImportUrl("");
    }
  };

  const handleImportUrlChange = (value: string) => {
    setImportUrl(value);
    setImportUrlDirty(value.trim() !== "");
  };

  const handleAdd = async (candidateId: string) => {
    setError("");
    if (target.kind === "campaign") {
      const result = await addToCampaign({
        input: { campaignId: target.campaignId, candidateId, stage: target.stage },
      });
      const errs = result.data?.addCandidateToCampaign?.errors;
      if (errs && errs.length > 0) {
        setError(errs.join(", "));
        return;
      }
    } else if (target.kind === "talent_pool") {
      const result = await addToPool({
        input: { talentPoolId: target.talentPoolId, candidateId },
      });
      const errs = result.data?.addToTalentPool?.errors;
      if (errs && errs.length > 0) {
        setError(errs.join(", "));
        return;
      }
    }
    onDone(candidateId);
  };

  const handleImport = async () => {
    if (!parsedImportUrl) return;
    setError("");

    let task: BackgroundTask | undefined;
    let errs: string[] | undefined;
    let existingId: string | undefined;
    if (target.kind === "campaign") {
      const result = await importToCampaign({
        input: { linkedinUrl: parsedImportUrl, campaignId: target.campaignId, stage: target.stage },
      });
      const payload = result.data?.importLinkedinToCampaign;
      errs = payload?.errors;
      task = payload?.backgroundTask;
      existingId = payload?.campaignCandidate?.candidate?.id;
    } else if (target.kind === "talent_pool") {
      const result = await importToPool({
        input: { linkedinUrl: parsedImportUrl, talentPoolId: target.talentPoolId },
      });
      const payload = result.data?.importLinkedinToTalentPool;
      errs = payload?.errors;
      task = payload?.backgroundTask;
      existingId = payload?.talentPoolMembership?.candidate?.id;
    } else {
      const result = await importToOrg({ input: { linkedinUrl: parsedImportUrl } });
      const payload = result.data?.importLinkedinProfile;
      errs = payload?.errors;
      task = payload?.backgroundTask;
      existingId = payload?.candidate?.id;
    }

    if (errs && errs.length > 0) {
      setError(errs.join(", "));
      return;
    }
    if (task) {
      upsertBackgroundTask(task);
      setPhase({ step: "importing", taskId: task.id, url: parsedImportUrl });
      return;
    }
    onDone(existingId ?? undefined);
  };

  const handleStartCreate = () => {
    setForm({ ...INITIAL_CANDIDATE_FORM, ...parseNameFromSearch(trimmed) });
    setManualOpen(true);
    setFocusManual(true);
    setError("");
  };

  const handleDuplicateCheck = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setChecking(true);

    try {
      const result = await client
        .query(
          CANDIDATE_DUPLICATE_CHECK_QUERY,
          {
            firstName: form.firstName,
            lastName: form.lastName,
            email: form.email || undefined,
            currentCompany: form.currentCompany || undefined,
          },
          { requestPolicy: "network-only" },
        )
        .toPromise();

      const duplicates: SearchCandidate[] = result.data?.candidateDuplicateCheck ?? [];

      if (duplicates.length === 0) {
        await handleCreate();
      } else {
        setPhase({ step: "duplicateReview", duplicates });
      }
    } finally {
      setChecking(false);
    }
  };

  const handleCreate = async () => {
    setError("");
    const input = buildCandidateInput(form);
    const addTo = buildAddTo(target);
    if (addTo) input.addTo = addTo;

    const result = await createCandidate({ input });
    const payload = result.data?.createCandidate;
    const errs = payload?.errors;
    if (errs && errs.length > 0) {
      setError(errs.join(", "));
      return;
    }
    if (payload?.candidate?.id) {
      onDone(payload.candidate.id);
    }
  };

  // A search-result or duplicate row: "Open" for the org view, a muted note
  // when the person is already in the target, otherwise the row adds them.
  const renderCandidateRow = (c: SearchCandidate) => {
    if (target.kind === "org") {
      return (
        <div key={c.id} className="block w-full">
          <ResultRow candidate={c} action={<OpenLink candidateId={c.id} />} />
        </div>
      );
    }
    const membership = membershipOf(c, target);
    if (membership) {
      return (
        <div key={c.id} className="block w-full">
          <ResultRow
            candidate={c}
            action={
              <span className="ml-2 flex-shrink-0 text-xs text-text-faint">
                {describeMembership(membership)}
              </span>
            }
          />
        </div>
      );
    }
    return (
      <button
        key={c.id}
        type="button"
        onClick={() => handleAdd(c.id)}
        disabled={adding}
        className="block w-full hover:bg-card-hover disabled:opacity-50"
      >
        <ResultRow
          candidate={c}
          action={<Plus size={14} className="ml-2 flex-shrink-0 text-accent" />}
        />
      </button>
    );
  };

  const renderUrlMatchAction = (c: SearchCandidate) => {
    if (target.kind === "org") return <OpenLink candidateId={c.id} />;
    const membership = membershipOf(c, target);
    if (membership) {
      return (
        <span className="ml-2 flex-shrink-0 text-xs text-text-faint">
          {describeMembership(membership)}
        </span>
      );
    }
    return (
      <button
        type="button"
        onClick={() => handleAdd(c.id)}
        disabled={adding}
        className={`ml-2 flex-shrink-0 ${PRIMARY_BUTTON}`}
      >
        {adding ? "Adding..." : addLabel(target)}
      </button>
    );
  };

  // For the org view the duplicate rows open the candidate instead of adding.
  const renderDuplicateRow = (c: SearchCandidate) => (
    <button
      key={c.id}
      type="button"
      onClick={() => handleAdd(c.id)}
      disabled={adding}
      className="block w-full hover:bg-card-hover disabled:opacity-50"
    >
      <ResultRow
        candidate={c}
        action={
          target.kind === "org" ? (
            <span className="ml-2 flex-shrink-0 text-xs text-accent">Open</span>
          ) : (
            <Plus size={14} className="ml-2 flex-shrink-0 text-accent" />
          )
        }
      />
    </button>
  );

  const displayName = trimmed || "candidate";
  const importUrlInvalid = importUrl.trim() !== "" && parsedImportUrl === null;

  return (
    <div className="space-y-3 rounded-lg border border-border bg-card-alt p-4">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-medium text-text-secondary">
          {heading ?? defaultHeading(target)}
        </h4>
        <button
          type="button"
          onClick={onCancel}
          aria-label="Close"
          className="rounded p-1 text-text-faint hover:bg-card-hover"
        >
          <X size={16} />
        </button>
      </div>

      {phase.step === "main" && (
        <>
          <div className="relative">
            <Search size={14} className="absolute left-2.5 top-2.5 text-text-faint" />
            <input
              type="text"
              value={search}
              onChange={(e) => handleSearchChange(e.target.value)}
              className="w-full rounded border border-input-border bg-card py-1.5 pl-8 pr-3 text-sm text-text-primary focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
              placeholder="Search by name or paste a LinkedIn URL..."
            />
          </div>

          {nameTerm !== null && (
            <>
              <div className="max-h-64 overflow-y-auto rounded border border-border bg-card">
                {nameFetching ? (
                  <p className="px-3 py-2 text-xs text-text-faint">Searching...</p>
                ) : nameCandidates.length === 0 ? (
                  <p className="px-3 py-3 text-xs text-text-faint">No candidates found.</p>
                ) : (
                  nameCandidates.map(renderCandidateRow)
                )}
              </div>

              {nameSearchReady && (
                <button
                  type="button"
                  onClick={handleStartCreate}
                  className="flex items-center gap-2 rounded-md border border-accent/30 bg-accent/10 px-3 py-1.5 text-xs font-medium text-accent hover:bg-accent/20"
                >
                  <UserPlus size={12} />
                  Create new candidate &ldquo;{displayName}&rdquo;
                </button>
              )}
            </>
          )}

          <section
            aria-label="Import from LinkedIn"
            className="space-y-2 rounded border border-border bg-card p-3"
          >
            <h5 className="flex items-center gap-2 text-xs font-medium text-text-secondary">
              <Linkedin size={12} className="text-[#0A66C2]" />
              Import from LinkedIn
            </h5>
            <label htmlFor={importInputId} className="sr-only">
              LinkedIn profile URL
            </label>
            <input
              id={importInputId}
              type="url"
              value={importUrl}
              onChange={(e) => handleImportUrlChange(e.target.value)}
              placeholder="https://www.linkedin.com/in/..."
              className="w-full rounded border border-input-border bg-card px-2 py-1.5 text-sm text-text-primary focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
            />
            {importUrlInvalid && (
              <p className="text-xs text-text-faint">
                Enter a LinkedIn profile URL (linkedin.com/in/…)
              </p>
            )}
            {urlMatch ? (
              <div className="rounded border border-border">
                <ResultRow candidate={urlMatch} action={renderUrlMatchAction(urlMatch)} />
              </div>
            ) : (
              <button
                type="button"
                onClick={handleImport}
                disabled={importing || urlFetching || parsedImportUrl === null}
                className="flex items-center gap-2 rounded-md bg-[#0A66C2] px-3 py-1.5 text-xs font-medium text-white hover:bg-[#004182] disabled:opacity-50"
              >
                <Linkedin size={12} />
                {importing
                  ? "Starting import..."
                  : urlFetching
                    ? "Checking..."
                    : importCtaLabel(target)}
              </button>
            )}
          </section>

          <CollapsibleSection
            title="Add manually"
            expanded={manualOpen}
            onToggle={() => setManualOpen((v) => !v)}
            className="rounded border border-border bg-card px-3"
          >
            <form onSubmit={handleDuplicateCheck} className="space-y-3 pb-3">
              <p className="text-xs text-text-muted">
                Have a LinkedIn URL? Import it above instead.
              </p>
              <CandidateFormFields
                form={form}
                onChange={setForm}
                autoFocusFirstField={manualOpen && focusManual}
              />
              <div className="flex justify-end">
                <button
                  type="submit"
                  disabled={checking || !form.firstName || !form.lastName}
                  className={PRIMARY_BUTTON}
                >
                  {checking ? "Checking..." : "Continue"}
                </button>
              </div>
            </form>
          </CollapsibleSection>

          <div className="flex justify-end">
            <button type="button" onClick={onCancel} className={SECONDARY_BUTTON}>
              Cancel
            </button>
          </div>
        </>
      )}

      {importPhase &&
        (importState === "failed" ? (
          <div className="space-y-3">
            <p className="text-sm text-red-600 dark:text-red-400">
              Import failed{importTask?.errorMessage ? `: ${importTask.errorMessage}` : ""}.
            </p>
            <div className="flex justify-end gap-2">
              <button type="button" onClick={onCancel} className={SECONDARY_BUTTON}>
                Close
              </button>
              <button
                type="button"
                onClick={() => setPhase({ step: "main" })}
                className={PRIMARY_BUTTON}
              >
                Try again
              </button>
            </div>
          </div>
        ) : importState === "succeeded" ? (
          <div className="space-y-3">
            <p className="text-sm text-text-secondary">
              {importedSummary(importedCandidate?.fullName, target)}
            </p>
            {importedFetching && <p className="text-xs text-text-faint">Loading candidate...</p>}
            {importedCandidate && (
              <div className="rounded border border-border bg-card">
                <ResultRow
                  candidate={importedCandidate}
                  action={<OpenLink candidateId={importedCandidate.id} />}
                />
              </div>
            )}
            <div className="flex justify-end gap-2">
              <button type="button" onClick={resetToMain} className={SECONDARY_BUTTON}>
                Add another
              </button>
              <button type="button" onClick={() => onDone()} className={PRIMARY_BUTTON}>
                Done
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <p className="flex items-center gap-2 text-sm text-text-secondary">
              <Loader2 size={14} className="animate-spin text-accent" />
              Importing {importPhase.url}…
            </p>
            <p className="text-xs text-text-muted">
              Fetching the profile from LinkedIn usually takes under a minute. You can close this
              and keep working; progress stays in the activity indicator at the top of the page.
            </p>
            <div className="flex justify-end">
              <button type="button" onClick={onCancel} className={SECONDARY_BUTTON}>
                Close
              </button>
            </div>
          </div>
        ))}

      {phase.step === "duplicateReview" && (
        <div className="space-y-3">
          <button
            type="button"
            onClick={() => {
              setPhase({ step: "main" });
              setManualOpen(true);
            }}
            className="flex items-center gap-1 text-xs text-text-muted hover:text-text-secondary"
          >
            <ArrowLeft size={12} />
            Back to form
          </button>

          <p className="text-xs text-text-secondary">
            We found {phase.duplicates.length} potential{" "}
            {phase.duplicates.length === 1 ? "match" : "matches"}. Did you mean one of these?
          </p>

          <div className="max-h-64 overflow-y-auto rounded border border-border bg-card">
            {phase.duplicates.map(renderDuplicateRow)}
          </div>

          <div className="flex justify-end">
            <button
              type="button"
              onClick={handleCreate}
              disabled={creating}
              className={SECONDARY_BUTTON}
            >
              {creating ? "Creating..." : "Create anyway"}
            </button>
          </div>
        </div>
      )}

      {error && <p className="text-xs text-red-600 dark:text-red-400">{error}</p>}
    </div>
  );
}
